import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { USER_ROLE, getUserRoleName } from '../../common/user';
import { User } from '../../common/user';
import { UserService } from '../../user/user.service';
import { OrganisationService } from '../organisation.service';
import { OrganisationUserDialogComponent } from './organisation-user-dialog.component';
import { ConfirmDialogComponent, ConfirmDialogModel } from '../../ui-components/dialogs/confirm-dialog.component';
import { Column } from '../../ui-components/table/table-component';
import { Organisation } from '../../common/organisation';

// a user as the organisation sees them: their role here and whether they have accepted the invite
type OrganisationUser = User & { activated?:boolean }

// what the table shows for a user; actions look the user up again by _key
interface UserRow {
    _key:string
    name:string
    email:string
    role:string
    status:string
    createdDate:Date
    signInDate:Date
    reminder:string
    canDelete:boolean
}

@Component({
    selector: 'app-organisation-users',
    templateUrl: './organisation-users.html',
    styleUrls: ['./organisation-users.scss'],
    standalone: false
})
export class OrganisationUsersComponent implements OnChanges {
    @Input() organisation:Organisation
    @Input() users:OrganisationUser[]

    columns:Column[] = []
    data:UserRow[] = []
    summary:{ label:string, value:number, hint:string }[] = []

    constructor(public dialog: MatDialog, private userService: UserService, private organisationService:OrganisationService) {
    }

    ngOnChanges(changes: SimpleChanges): void {
        if ((changes["organisation"] || changes["users"]) && this.users && this.organisation) {
            this.refresh()
        }
    }

    private refresh() {
        const admin = this.hasAdminAccess()
        const me = this.userService.getUser()?._key

        this.data = this.users
            .map(u => ({
                _key: u._key,
                name: u._key === me ? `${u.name} (you)` : u.name,
                email: u.email,
                role: getUserRoleName(u.role),
                status: u.activated ? 'Active' : 'Invite pending',
                createdDate: u.createdDate,
                signInDate: u.signInDate,
                reminder: admin && !u.activated ? 'Resend invite' : '',
                canDelete: admin && this.canRemove(u),
            }))
            // admins first, then by name
            .sort((a, b) => (this.isAdmin(b) ? 1 : 0) - (this.isAdmin(a) ? 1 : 0) || (a.name || '').localeCompare(b.name || ''))

        const pending = this.users.filter(u => !u.activated).length
        this.columns = [
            new Column('name', 'Name', 'string'),
            new Column('email', 'Email', 'string'),
            admin ? new Column('role', 'Role', 'button', 'Change user role', 'accent') : new Column('role', 'Role', 'string'),
            new Column('status', 'Status', 'string'),
            new Column('createdDate', 'Joined', 'date'),
            new Column('signInDate', 'Last login', 'date'),
        ]
        if (admin && pending > 0) {
            this.columns.push(new Column('reminder', 'Invite', 'button', 'Email the user again, reminding them to accept the invite', 'accent'))
        }
        if (admin) {
            this.columns.push(new Column('canDelete', 'Remove', 'icon-button', 'Remove from organisation', 'warn', 'person_remove'))
        }

        this.summary = [
            { label: 'Users', value: this.users.length, hint: 'Users with access to this organisation' },
            { label: 'Admins', value: this.users.filter(u => u.role === USER_ROLE.ADMIN).length, hint: 'Users who can manage the organisation, its users and billing' },
            { label: 'Invites pending', value: pending, hint: "Invited users who haven't accepted yet" },
        ]
    }

    private isAdmin(row:UserRow) {
        return this.users.find(u => u._key === row._key)?.role === USER_ROLE.ADMIN
    }

    async handleRowEvent(rowEvent:{row:any, column?:Column}) {
        const user = rowEvent.row && this.users.find(u => u._key === rowEvent.row._key)
        if (!user || !rowEvent.column || !this.hasAdminAccess()) return
        if (rowEvent.column.name === "canDelete") this.removeUser(user)
        else if (rowEvent.column.name === "role") this.updateUser(user)
        else if (rowEvent.column.name === "reminder") this.reInvite(user)
    }

    // the organisation must keep at least one admin
    canRemove(user:User) {
        return user.role !== USER_ROLE.ADMIN || this.users.some(u => u._key !== user._key && u.role === USER_ROLE.ADMIN)
    }

    public updateUser(user:User) {
        const dialogRef = this.dialog.open(OrganisationUserDialogComponent, {
            data: {users:this.users, user},
            hasBackdrop:true
        });

        dialogRef.afterClosed().subscribe(async (role:USER_ROLE) => {
            if (role !== undefined && role !== user.role) {
                await this.organisationService.updateOrganisationAccess(user, role)
            }
        })
    }

    public removeUser(user:User) {
        if (!this.canRemove(user)) return

        const self = user._key === this.userService.getUser()?._key
        const message = self
            ? `Are you sure you want to remove yourself from ${this.organisation.name}? You will lose access to it until an admin invites you again.`
            : `Are you sure you want to remove ${user.name} from ${this.organisation.name}? They will lose access to its projects and models.`
        const dialogData = new ConfirmDialogModel("Remove user", [message], "remove", false, "Remove", "Cancel");

        const dialogRef = this.dialog.open(ConfirmDialogComponent, {
            maxWidth: "450px",
            data: dialogData
        });

        dialogRef.afterClosed().subscribe(async dialogResult => {
            if (dialogResult?.button === "Remove") {
                await this.organisationService.removeOrganisationAccess(user)
            }
        })
    }

    hasAdminAccess() {
        return this.organisation?.role === USER_ROLE.ADMIN
    }

    reInvite(user:OrganisationUser) {
        if (!user.activated) this.userService.inviteUser(this.organisation, user)
    }
}
